import { AfterViewInit, booleanAttribute, Directive, ElementRef, effect, inject, input, OnDestroy } from '@angular/core';

export enum TooltipPlacement {
	Top = 'top',
	Bottom = 'bottom',
	Left = 'left',
	Right = 'right'
}

const NATIVELY_FOCUSABLE_TAGS = ['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'SUMMARY'];

@Directive({
	selector: '[msTooltip]',
	standalone: true
})
export class TooltipDirective implements AfterViewInit, OnDestroy {
	public msTooltip = input<string>('');
	public msTooltipDisabled = input(false, { transform: booleanAttribute });
	public msTooltipPlacement = input<TooltipPlacement | undefined>(undefined);
	public msTooltipOnlyIfTruncated = input(false, { transform: booleanAttribute });

	private el: ElementRef<HTMLElement> = inject(ElementRef);
	private tooltipInstance: any = null;
	private currentPlacement: TooltipPlacement | null = null;
	private resizeObserver: ResizeObserver | null = null;
	private viewInitialized = false;
	private hasSetTabindex = false;
	private hasSetAriaLabel = false;
	private focusInHandler: (() => void) | null = null;
	private focusOutHandler: (() => void) | null = null;
	private shownHandler: (() => void) | null = null;
	private hiddenHandler: (() => void) | null = null;
	private scrollDismissHandler: (() => void) | null = null;

	constructor() {
		effect(() => {
			this.msTooltip();
			this.msTooltipDisabled();
			this.msTooltipPlacement();
			const truncatedMode = this.msTooltipOnlyIfTruncated();

			if (this.viewInitialized) {
				this.applyAccessibility();
				this.syncResizeObserver(truncatedMode);
				this.refresh();
			}
		});
	}

	public ngAfterViewInit(): void {
		this.viewInitialized = true;
		this.applyAccessibility();
		this.syncResizeObserver(this.msTooltipOnlyIfTruncated());
		this.refresh();
	}

	public ngOnDestroy(): void {
		this.disposeTooltip();
		this.resizeObserver?.disconnect();
		this.resizeObserver = null;
	}

	private refresh(): void {
		const text = this.msTooltip();
		const disabled = this.msTooltipDisabled();
		const truncatedMode = this.msTooltipOnlyIfTruncated();
		const truncated = truncatedMode && this.isTruncated();

		const shouldShow = !!text && !disabled && (!truncatedMode || truncated);

		if (truncatedMode) {
			// Truncated-mode hosts (<span>, <div>, <td>) aren't natively focusable, so the
			// hover-focus tooltip trigger silently degrades to hover-only for keyboard users.
			// Adding `tabindex` blanket on every host would bloat the tab order with cells
			// that have nothing to reveal, so only add a tab stop when the tooltip would
			// actually fire — i.e. text is set, not disabled, and the host is truncated.
			this.syncTruncatedTabindex(shouldShow);
		}

		if (shouldShow) {
			this.createOrUpdateTooltip(text);
		} else {
			this.disposeTooltip();
		}
	}

	private isTruncated(): boolean {
		const el = this.el.nativeElement;
		return el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;
	}

	private createOrUpdateTooltip(text: string): void {
		const bootstrap = (window as any).bootstrap;
		if (!bootstrap?.Tooltip) return;

		const placement = this.effectivePlacement();

		// Bootstrap bakes placement at construction time and exposes no runtime update,
		// so changes to msTooltipPlacement (or context-default flips when truncated mode
		// toggles) require recreating the instance.
		if (this.tooltipInstance && this.currentPlacement !== placement) {
			this.disposeTooltip();
		}

		if (this.tooltipInstance) {
			// Keep Bootstrap's canonical title in sync — setContent only updates the
			// visible inner; data-bs-original-title is what Bootstrap restores on dispose
			// and what a11y tools may inspect.
			this.el.nativeElement.setAttribute('data-bs-original-title', text);
			this.tooltipInstance.setContent({ '.tooltip-inner': text });
			return;
		}

		this.el.nativeElement.setAttribute('title', text);
		this.tooltipInstance = new bootstrap.Tooltip(this.el.nativeElement, {
			// Hover only — focus is driven from attachTriggerHandlers so that a mouse click can't pin
			// the tooltip open. See that method for why Bootstrap's own focus trigger isn't used.
			trigger: 'hover',
			placement: placement
		});
		this.currentPlacement = placement;
		this.attachTriggerHandlers();
	}

	/**
	 * Bootstrap's `focus` trigger cannot tell a keyboard tab from a mouse click, and clicking focuses
	 * the host — which pins the tooltip open until focus moves somewhere else, since the matching
	 * mouseleave refuses to hide anything while another trigger is still active. Driving focus here
	 * instead keeps the keyboard parity while ignoring the focus a click produces.
	 *
	 * A shown tooltip is appended to <body>, so it is not clipped by whatever the page scrolls inside.
	 * Left alone, scrolling a table slides the row out of view while its tooltip carries on across the
	 * page. Dismissing on scroll avoids that, and is what a tooltip should do anyway; the listener is
	 * bound only while one is actually on screen, and in the capture phase so scrolls inside nested
	 * containers count, not just the window.
	 */
	private attachTriggerHandlers(): void {
		const el = this.el.nativeElement;

		this.focusInHandler = () => {
			if (this.isKeyboardFocused(el)) this.tooltipInstance?.show();
		};
		this.focusOutHandler = () => this.tooltipInstance?.hide();
		this.scrollDismissHandler = () => this.tooltipInstance?.hide();
		this.shownHandler = () => document.addEventListener('scroll', this.scrollDismissHandler!, true);
		this.hiddenHandler = () => document.removeEventListener('scroll', this.scrollDismissHandler!, true);

		el.addEventListener('focusin', this.focusInHandler);
		el.addEventListener('focusout', this.focusOutHandler);
		el.addEventListener('shown.bs.tooltip', this.shownHandler);
		el.addEventListener('hidden.bs.tooltip', this.hiddenHandler);
	}

	private detachTriggerHandlers(): void {
		const el = this.el.nativeElement;

		if (this.focusInHandler) el.removeEventListener('focusin', this.focusInHandler);
		if (this.focusOutHandler) el.removeEventListener('focusout', this.focusOutHandler);
		if (this.shownHandler) el.removeEventListener('shown.bs.tooltip', this.shownHandler);
		if (this.hiddenHandler) el.removeEventListener('hidden.bs.tooltip', this.hiddenHandler);
		if (this.scrollDismissHandler) document.removeEventListener('scroll', this.scrollDismissHandler, true);

		this.focusInHandler = null;
		this.focusOutHandler = null;
		this.shownHandler = null;
		this.hiddenHandler = null;
		this.scrollDismissHandler = null;
	}

	private isKeyboardFocused(el: HTMLElement): boolean {
		try {
			return el.matches(':focus-visible');
		} catch {
			// matches() throws rather than returning false when a browser doesn't know the selector,
			// so fall back to treating any focus as keyboard focus — the old behaviour.
			return true;
		}
	}

	private disposeTooltip(): void {
		this.detachTriggerHandlers();

		if (this.tooltipInstance) {
			this.tooltipInstance.dispose();
			this.tooltipInstance = null;
			this.currentPlacement = null;
		}
		this.el.nativeElement.removeAttribute('title');
	}

	private syncResizeObserver(truncatedMode: boolean): void {
		if (truncatedMode && !this.resizeObserver) {
			this.resizeObserver = new ResizeObserver(() => this.refresh());
			this.resizeObserver.observe(this.el.nativeElement);
		} else if (!truncatedMode && this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}
	}

	private syncTruncatedTabindex(shouldBeFocusable: boolean): void {
		const el = this.el.nativeElement;
		if (this.isNativelyFocusable(el)) return;

		if (shouldBeFocusable && !this.hasSetTabindex && !el.hasAttribute('tabindex')) {
			el.setAttribute('tabindex', '0');
			this.hasSetTabindex = true;
		} else if (!shouldBeFocusable && this.hasSetTabindex) {
			el.removeAttribute('tabindex');
			this.hasSetTabindex = false;
		}
	}

	private effectivePlacement(): TooltipPlacement {
		const explicit = this.msTooltipPlacement();
		if (explicit) return explicit;
		return this.msTooltipOnlyIfTruncated() ? TooltipPlacement.Bottom : TooltipPlacement.Top;
	}

	private applyAccessibility(): void {
		const el = this.el.nativeElement;
		const truncatedMode = this.msTooltipOnlyIfTruncated();

		// Always-on mode: set `tabindex` once so the hover-focus trigger has a focus target
		// on non-focusable hosts. Truncated mode handles `tabindex` dynamically in
		// `syncTruncatedTabindex()` so we only add tab stops to cells actually truncated.
		if (!truncatedMode && !this.hasSetTabindex) {
			if (!this.isNativelyFocusable(el) && !el.hasAttribute('tabindex')) {
				el.setAttribute('tabindex', '0');
				this.hasSetTabindex = true;
			}
		}

		// `aria-label` fallback only applies to always-on mode (icon-only triggers without
		// visible text). Truncated-mode hosts have visible content — the accessible name
		// is already in the host's `textContent`.
		if (truncatedMode) return;

		const text = this.msTooltip();
		const hasVisibleText = !!(el.textContent ?? '').trim();
		const callerOwnsAria =
			el.hasAttribute('aria-labelledby') ||
			(el.hasAttribute('aria-label') && !this.hasSetAriaLabel);

		if (!hasVisibleText && !callerOwnsAria && text) {
			el.setAttribute('aria-label', text);
			this.hasSetAriaLabel = true;
		}
	}

	private isNativelyFocusable(el: HTMLElement): boolean {
		if (NATIVELY_FOCUSABLE_TAGS.includes(el.tagName)) return true;
		if (el.tagName === 'A' && el.hasAttribute('href')) return true;
		return false;
	}
}
